import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AssessmentNavigation from '../../components/ui/AssessmentNavigation';
import ProductionCapacityForm from './components/ProductionCapacityForm';
import ProcessFlowMapper from './components/ProcessFlowMapper';
import QualityControlSection from './components/QualityControlSection';
import ProductionLossTracker from './components/ProductionLossTracker';
import BufferCapacityEvaluation from './components/BufferCapacityEvaluation';
import OperationalMetricsPanel from './components/OperationalMetricsPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const OperationalAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('capacity');
  const [errors, setErrors] = useState({});

  const [capacityData, setCapacityData] = useState({
    dailyProductionVolume: '',
    maxCapacity: '',
    shiftPattern: '',
    productionStaff: '',
    utilizationRate: '',
    operationalHours: ''
  });

  const [processStages, setProcessStages] = useState([
    {
      id: 1,
      name: 'Penerimaan Susu',
      duration: '30',
      bottleneck: false,
      riskLevel: 'low',
      notes: ''
    },
    {
      id: 2,
      name: 'Pasteurisasi',
      duration: '45',
      bottleneck: true,
      riskLevel: 'high',
      notes: 'Kapasitas terbatas pada jam puncak'
    },
    {
      id: 3,
      name: 'Pengemasan',
      duration: '60',
      bottleneck: false,
      riskLevel: 'medium',
      notes: ''
    }
  ]);

  const [qualityData, setQualityData] = useState({
    totalMilkReceived: '',
    rejectedMilk: '',
    qualityCheckpoints: '',
    testingFrequency: '',
    avgTestingTime: '',
    qcStaff: ''
  });

  const [lossData, setLossData] = useState({
    totalProduction: '',
    spillageLoss: '',
    contaminationLoss: '',
    equipmentLoss: '',
    qualityLoss: '',
    otherLoss: '',
    primaryLossType: ''
  });

  const [bufferData, setBufferData] = useState({
    storageCapacity: '',
    dailyConsumption: '',
    currentStock: '',
    restockTime: '',
    backupSuppliers: '',
    coolingCapacity: ''
  });

  const calculateMetrics = () => {
    const rejectionRate = qualityData?.totalMilkReceived && qualityData?.rejectedMilk
      ? ((parseFloat(qualityData?.rejectedMilk) / parseFloat(qualityData?.totalMilkReceived)) * 100)?.toFixed(2)
      : 0;

    const productionLoss = lossData?.totalProduction
      ? (([lossData?.spillageLoss, lossData?.contaminationLoss, lossData?.equipmentLoss, lossData?.qualityLoss, lossData?.otherLoss]?.reduce((sum, val) => sum + (parseFloat(val) || 0), 0) / parseFloat(lossData?.totalProduction)) * 100)?.toFixed(2)
      : 0;

    const bufferDays = bufferData?.storageCapacity && bufferData?.dailyConsumption
      ? (parseFloat(bufferData?.storageCapacity) / parseFloat(bufferData?.dailyConsumption))?.toFixed(1)
      : 0;

    const resilienceScore = bufferDays >= 7 ? 95 : bufferDays >= 5 ? 80 : bufferDays >= 3 ? 60 : bufferDays >= 1 ? 40 : 20;

    return {
      utilizationRate: capacityData?.utilizationRate || 0,
      rejectionRate,
      productionLoss,
      bufferDays,
      resilienceScore,
      criticalBottlenecks: processStages?.filter(s => s?.bottleneck)?.length,
      productionStaff: capacityData?.productionStaff || 0
    };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!capacityData?.dailyProductionVolume) newErrors.dailyProductionVolume = 'Volume produksi harian wajib diisi';
    if (!capacityData?.maxCapacity) newErrors.maxCapacity = 'Kapasitas maksimal wajib diisi';
    if (!capacityData?.shiftPattern) newErrors.shiftPattern = 'Pola shift wajib dipilih';
    if (!capacityData?.productionStaff) newErrors.productionStaff = 'Jumlah staff wajib diisi';
    if (!capacityData?.utilizationRate) newErrors.utilizationRate = 'Tingkat utilisasi wajib diisi';
    if (!capacityData?.operationalHours) newErrors.operationalHours = 'Jam operasional wajib diisi';

    if (!qualityData?.totalMilkReceived) newErrors.totalMilkReceived = 'Total susu diterima wajib diisi';
    if (!qualityData?.rejectedMilk) newErrors.rejectedMilk = 'Susu ditolak wajib diisi';
    if (!qualityData?.qualityCheckpoints) newErrors.qualityCheckpoints = 'Jumlah checkpoint wajib diisi';
    if (!qualityData?.testingFrequency) newErrors.testingFrequency = 'Frekuensi testing wajib diisi';

    if (!lossData?.totalProduction) newErrors.totalProduction = 'Total produksi wajib diisi';

    if (!bufferData?.storageCapacity) newErrors.storageCapacity = 'Kapasitas penyimpanan wajib diisi';
    if (!bufferData?.dailyConsumption) newErrors.dailyConsumption = 'Konsumsi harian wajib diisi';
    if (!bufferData?.currentStock) newErrors.currentStock = 'Stok saat ini wajib diisi';

    if (processStages?.length === 0) {
      newErrors.processStages = 'Minimal satu tahapan proses harus ditambahkan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      navigate('/equipment-analysis');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    navigate('/supplier-management');
  };

  const sections = [
    { id: 'capacity', label: 'Kapasitas Produksi', icon: 'Factory' },
    { id: 'process', label: 'Alur Proses', icon: 'GitBranch' },
    { id: 'quality', label: 'Kontrol Kualitas', icon: 'ClipboardCheck' },
    { id: 'loss', label: 'Production Loss', icon: 'TrendingDown' },
    { id: 'buffer', label: 'Buffer & Resiliensi', icon: 'Shield' },
    { id: 'metrics', label: 'Metrik Real-time', icon: 'BarChart3' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={3} completedSteps={[1, 2]} />
      <AssessmentNavigation completedModules={['supplier']} />
      <main className="pt-44 pb-24">
        <div className="container-executive space-executive">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">Penilaian Operasional</h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
              Evaluasi komprehensif proses produksi, identifikasi bottleneck, dan analisis efisiensi operasional untuk scale-up readiness
            </p>
          </div>

          {Object.keys(errors)?.length > 0 && (
            <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error/20 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error">Terdapat {Object.keys(errors)?.length} kesalahan pada form</p>
                <p className="text-xs text-muted-foreground mt-1">Mohon lengkapi semua field yang wajib diisi</p>
              </div>
            </div>
          )}

          <div className="mb-8">
            <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-lg">
              {sections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`flex-1 min-w-[140px] px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    activeSection === section?.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-transparent text-muted-foreground hover:bg-card'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name={section?.icon} size={18} />
                    <span className="hidden md:inline">{section?.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6 lg:space-y-8">
            {activeSection === 'capacity' && (
              <ProductionCapacityForm
                formData={capacityData}
                onChange={setCapacityData}
                errors={errors}
              />
            )}

            {activeSection === 'process' && (
              <ProcessFlowMapper
                processStages={processStages}
                onUpdateStages={setProcessStages}
              />
            )}

            {activeSection === 'quality' && (
              <QualityControlSection
                formData={qualityData}
                onChange={setQualityData}
                errors={errors}
              />
            )}

            {activeSection === 'loss' && (
              <ProductionLossTracker
                lossData={lossData}
                onChange={setLossData}
                errors={errors}
              />
            )}

            {activeSection === 'buffer' && (
              <BufferCapacityEvaluation
                bufferData={bufferData}
                onChange={setBufferData}
                errors={errors}
              />
            )}

            {activeSection === 'metrics' && (
              <OperationalMetricsPanel metrics={calculateMetrics()} />
            )}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBack}
            >
              Kembali ke Manajemen Pemasok
            </Button>
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={handleSubmit}
            >
              Lanjut ke Analisis Peralatan
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OperationalAssessment;